import { StyleSheet, css } from 'aphrodite';
import * as React from 'react';
import { useParams } from 'react-router';
// Note: this file uses legacy `web/styles/colors` and the legacy `LoadingSpinner`
// to stay consistent with sibling pages in the `view` sub-app.
// THEME spacing/border-radius tokens are used inline where they don't drift from that pattern.
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { background, brandPrimary, charmGray, shuffleGray, titles, white } from '../../../web/styles/colors';
import { PublicNewsletterIframe } from '../../components/PublicNewsletterIframe';
import { IPublicNewsletterView } from '../../models/Api';
import { PublicNewsletterNotFoundError, usePublicNewsletterQuery } from '../../queries/usePublicNewsletterQuery';

const s = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		width: '100%',
		background,
	},
	header: {
		alignItems: 'center',
		backgroundColor: white,
		borderBottom: `1px solid ${shuffleGray}`,
		boxSizing: 'border-box',
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.xs,
		padding: `${THEME.spacing.lg}px ${THEME.spacing.lg}px`,
	},
	subjectRow: {
		alignItems: 'center',
		display: 'flex',
		gap: THEME.spacing.sm,
		justifyContent: 'center',
	},
	subject: {
		color: titles,
		fontSize: '1.25rem',
		fontWeight: 600,
		margin: 0,
		textAlign: 'center',
	},
	previewBadge: {
		backgroundColor: THEME.colors.pill.yellow.background,
		border: `1px solid ${THEME.colors.pill.yellow.border}`,
		borderRadius: 999,
		color: THEME.colors.pill.yellow.icon,
		fontSize: '0.75rem',
		fontWeight: 600,
		letterSpacing: '0.04em',
		padding: `${THEME.spacing.xs / 2}px ${THEME.spacing.sm}px`,
		textTransform: 'uppercase',
	},
	meta: {
		color: charmGray,
		fontSize: '0.875rem',
		textAlign: 'center',
	},
	content: {
		flex: 1,
		minHeight: 0,
		overflow: 'hidden',
		display: 'flex',
	},
	loadingContainer: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		justifyContent: 'center',
		padding: THEME.spacing.xl,
	},
	notFoundContainer: {
		alignItems: 'center',
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: THEME.spacing.md,
		justifyContent: 'center',
		padding: THEME.spacing.xl,
		textAlign: 'center',
	},
	notFoundTitle: {
		color: titles,
		fontSize: '1.5rem',
		fontWeight: 600,
		margin: 0,
	},
	notFoundBody: {
		color: charmGray,
		fontSize: '1rem',
		margin: 0,
		maxWidth: 480,
	},
	footer: {
		alignItems: 'center',
		backgroundColor: white,
		borderTop: `1px solid ${shuffleGray}`,
		color: charmGray,
		display: 'flex',
		fontSize: '0.75rem',
		justifyContent: 'center',
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
	},
	footerLink: {
		color: brandPrimary,
		marginLeft: THEME.spacing.xs,
		textDecoration: 'none',
	},
});

const formatSentDate = (sentDate: string) => {
	try {
		return new Date(sentDate).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});
	} catch {
		return sentDate;
	}
};

interface IManagedMetaTag {
	element: HTMLMetaElement;
	createdByEffect: boolean;
	previousContent: string | null;
}

const setMeta = (property: string, content: string): IManagedMetaTag => {
	const existing = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
	if (existing) {
		const previousContent = existing.getAttribute('content');
		existing.setAttribute('content', content);
		return { element: existing, createdByEffect: false, previousContent };
	}
	const el = document.createElement('meta');
	el.setAttribute('property', property);
	el.setAttribute('content', content);
	document.head.appendChild(el);
	return { element: el, createdByEffect: true, previousContent: null };
};

const restoreMeta = ({ element, createdByEffect, previousContent }: IManagedMetaTag) => {
	if (createdByEffect) {
		element.remove();
	} else if (previousContent !== null) {
		element.setAttribute('content', previousContent);
	} else {
		element.removeAttribute('content');
	}
};

const useDocumentMeta = (newsletter: IPublicNewsletterView | undefined) => {
	React.useEffect(() => {
		if (!newsletter) return;
		const previousTitle = document.title;
		document.title = newsletter.subject;

		const managed: IManagedMetaTag[] = [setMeta('og:title', newsletter.subject), setMeta('og:type', 'article')];

		return () => {
			document.title = previousTitle;
			managed.forEach(restoreMeta);
		};
	}, [newsletter]);
};

export function PublicNewsletterPage() {
	const params = useParams<{ publicShareCode: string }>();
	const query = usePublicNewsletterQuery({ publicShareCode: params.publicShareCode });
	useDocumentMeta(query.data);

	if (query.isLoading) {
		return (
			<div className={css(s.root)}>
				<div className={css(s.loadingContainer)}>
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	if (query.isError || !query.data) {
		const isNotFound = query.error instanceof PublicNewsletterNotFoundError;
		return (
			<div className={css(s.root)}>
				<div className={css(s.notFoundContainer)}>
					<h1 className={css(s.notFoundTitle)}>This newsletter isn't available</h1>
					<p className={css(s.notFoundBody)}>
						{isNotFound
							? 'The link may be incorrect, or the sender may have stopped sharing this newsletter publicly.'
							: 'Something went wrong loading this newsletter. Please try again later.'}
					</p>
				</div>
			</div>
		);
	}

	const newsletter = query.data;

	return (
		<div className={css(s.root)}>
			<header className={css(s.header)}>
				<div className={css(s.subjectRow)}>
					<h1 className={css(s.subject)}>{newsletter.subject}</h1>
					{newsletter.sentDate ? null : <span className={css(s.previewBadge)}>Preview</span>}
				</div>
				{newsletter.sentDate ? <div className={css(s.meta)}>{formatSentDate(newsletter.sentDate)}</div> : null}
			</header>
			<div className={css(s.content)}>
				<PublicNewsletterIframe htmlContent={newsletter.htmlContent} title={newsletter.subject} />
			</div>
			<footer className={css(s.footer)}>
				Powered by
				<a className={css(s.footerLink)} href='https://www.levitate.ai' target='_blank' rel='noopener noreferrer'>
					Levitate
				</a>
			</footer>
		</div>
	);
}
